import React, { useEffect, useState } from 'react';
import { Sparkles, Trash2, Loader2 } from 'lucide-react';
import { templatesApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

function TemplateCard({ template, onApply, onDelete }) {
  return (
    <div
      onClick={() => onApply(template)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onApply(template); } }}
      role="button"
      tabIndex={0}
      data-testid={`template-card-${template.id}`}
      className="text-left rounded-2xl bg-white border border-[#ECE6FA] p-4 hover:border-[#5B4FE8] hover:shadow-md transition-all group min-w-[260px] sm:min-w-0 flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#5B4FE8]/40"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-[#5B4FE8]">
          <Sparkles size={11} />
          {template.is_starter ? 'Starter' : 'Saved'}
        </span>
        {!template.is_starter && (
          <span
            onClick={(e) => { e.stopPropagation(); onDelete(template.id); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onDelete(template.id); } }}
            role="button"
            tabIndex={0}
            aria-label="Delete template"
            className="w-6 h-6 rounded-md flex items-center justify-center text-[#DC3545] hover:bg-[#FDECEC] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-pointer"
          >
            <Trash2 size={12} />
          </span>
        )}
      </div>
      <h4 className="mt-1 text-[14.5px] font-bold text-[#1A1533] truncate">{template.name}</h4>
      <p className="mt-1 text-[12px] text-[#4A4463] leading-relaxed line-clamp-2">{template.description}</p>
      <div className="mt-3 flex items-center gap-3 text-[11.5px] text-[#6B6485]">
        <span>{template.flow?.length ?? 0} modules</span>
        <span>•</span>
        <span>{template.total_duration} min</span>
      </div>
    </div>
  );
}

export default function TemplatesGallery({ onApply, refreshKey = 0 }) {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    templatesApi
      .list()
      .then((res) => { if (active) setTemplates(Array.isArray(res) ? res : []); })
      .catch(() => { if (active) setTemplates([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refreshKey]);

  const handleDelete = async (id) => {
    try {
      await templatesApi.remove(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast({ title: 'Template removed' });
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e?.message });
    }
  };

  if (loading) {
    return (
      <div className="px-5 md:px-10 pb-2 flex items-center gap-2 text-[#5B4FE8]">
        <Loader2 size={14} className="animate-spin" /> <span className="text-[12.5px]">Loading templates…</span>
      </div>
    );
  }

  if (templates.length === 0) return null;

  return (
    <div className="px-5 md:px-10 pb-4" data-testid="templates-gallery">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[14px] md:text-[15px] font-bold text-[#1A1533] inline-flex items-center gap-1.5">
          <Sparkles size={14} className="text-[#5B4FE8]" />
          Start from a template
        </h3>
        <span className="text-[11.5px] text-[#8A83A5]">Click to clone</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 -mx-1 px-1">
        {templates.map((t) => (
          <TemplateCard key={t.id} template={t} onApply={onApply} onDelete={handleDelete} />
        ))}
      </div>
    </div>
  );
}
